import React, { useEffect, useState } from 'react'
import HoverCard from 'components/hover-card'
import { Join } from 'components/icons'
import api from 'helpers/api'
import { useViewer } from 'hooks/useViewer'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { ColumnName, TableName, openLookups } from 'components/spreadsheet/components/modal/views/tablejoins'
import { ITableJoin, ITableViewWithColumns } from 'types'

// What a header's card reads, shared by the headers of a table's lookup columns and fetched once each: the
// table's lookups with their sources' names (the table's own copy leaves them out, as a source may be one
// the viewer can't open; this endpoint names each as the viewer may see it), and each source view's columns,
// for their icons (null for one that can't be opened).
// Every key starts with who's reading: what a source is named depends on who may see it, so another account (or a
// masqueraded one) never reads what was fetched for the last.
const lookupsOf = new Map<string, Promise<ITableJoin[]>>()
// The same once they've arrived, so a menu opening later has them at once, and sizes itself to fit them.
const lookupsKnown = new Map<string, ITableJoin[]>()
const sourceViews = new Map<string, Promise<ITableViewWithColumns | null>>()
const cached = <T,>(cache: Map<string, Promise<T>>, key: string, load: () => Promise<T>): Promise<T> => {
  if (!cache.has(key)) cache.set(key, load())
  return cache.get(key)!
}

/**
 * How a lookup column is filled, in a sentence: "Each row takes ⊙ Region from the row where ⊙ Country matches
 * its ⊙ Customer Country." The source's columns carry their type icons once its view has loaded.
 */
export const LookupSentence: React.FC<{ join: ITableJoin; columnId?: string }> = ({ join, columnId }) => {
  const { spreadsheetData } = useDataContext()
  const [view, setView] = useState<ITableViewWithColumns | null | undefined>(undefined)
  const who = useViewer()
  useEffect(() => {
    let current = true
    // Another source's columns, or another viewer's, aren't carried over while these are read.
    setView(undefined)
    cached(sourceViews, `${who}:${join.joinViewId}`, () =>
      Promise.resolve()
        .then(() => api.getTableView(join.joinViewId, { ignoreCachedOptions: true }))
        .then((response) => response.data)
        .catch(() => null)
    ).then((loaded) => current && setView(loaded))
    return () => {
      current = false
    }
  }, [join.joinViewId, who])

  const sourceColumn = (id: string, name?: string) => {
    const column = view?.columns.find((item) => item.publicId === id)
    return column ? <ColumnName column={column} /> : <span className="font-medium text-body">{name}</span>
  }
  const ownColumn = (id: string) => (
    <ColumnName column={spreadsheetData.tableDetails.columns.find((item) => item.publicId === id)} />
  )
  const looked = join.dataColumns.find((column) => column.targetColumnId === columnId)
  return (
    <span>
      Each row takes {looked ? sourceColumn(looked.sourceColumnId, looked.sourceColumnName) : 'its values'} from the{' '}
      {join.isOneToMany ? 'rows' : 'row'} where{' '}
      {join.joinColumns.map((rule, index) => (
        <React.Fragment key={index}>
          {index > 0 && ' and '}
          {sourceColumn(rule.sourceColumnId, rule.sourceColumnName)} matches its {ownColumn(rule.targetColumnId)}
        </React.Fragment>
      ))}
      .
    </span>
  )
}

/**
 * The lookup that fills a column, with its source's names (as the viewer may see them), once `wanted`: the
 * table's own copy of its lookups leaves the names out. Undefined until it's known.
 */
export const useColumnLookup = (columnId: string | undefined, wanted: boolean): ITableJoin | undefined => {
  const { spreadsheetData } = useDataContext()
  const table = spreadsheetData.tableDetails
  const joinId = table.joins?.find((item) =>
    item.dataColumns.some((column) => column.targetColumnId === columnId)
  )?.publicId
  // Keyed by the lookups as they stand, so a saved change is fetched afresh.
  const who = useViewer()
  const joinsKey = `${who}:${table.publicId}:${JSON.stringify(table.joins ?? [])}`
  const [join, setJoin] = useState<ITableJoin | undefined>(undefined)

  useEffect(() => {
    // Another column's lookup, or none, isn't carried over while this one's is read.
    setJoin(undefined)
    if (!wanted || !joinId) return
    let current = true
    cached(lookupsOf, joinsKey, () =>
      Promise.resolve()
        .then(() => api.getTableJoins(table.publicId))
        .then((response) => {
          lookupsKnown.set(joinsKey, response.data)
          return response.data
        })
        .catch(() => [])
    )
      .then((joins) => {
        if (current) setJoin(joins.find((item) => item.publicId === joinId))
      })
      .catch(() => undefined)
    return () => {
      current = false
    }
  }, [wanted, joinId, joinsKey])

  return join ?? (wanted ? lookupsKnown.get(joinsKey)?.find((item) => item.publicId === joinId) : undefined)
}

/**
 * The mark on a lookup column's header, in the blue of what acts on a column: pointing at it says where the
 * column's values come from and how the rows match; pressing it opens the table's lookups at that source.
 */
const LookupIndicator: React.FC<{ columnId?: string }> = ({ columnId }) => {
  const { spreadsheetData } = useDataContext()
  const table = spreadsheetData.tableDetails
  const joinId = table.joins?.find((item) =>
    item.dataColumns.some((column) => column.targetColumnId === columnId)
  )?.publicId
  // Fetched as the header shows (once for the table's lookups), so the card and the column's menu have them.
  const join = useColumnLookup(columnId, true)

  const open = () => openLookups({ tableId: table.publicId, joinId })
  // For an admin, a button: focusable, and pressed with Enter or Space as with the mouse.
  const mark = (
    <span
      className={`flex items-center justify-center rounded-sm text-blue focus-visible:outline-2 focus-visible:outline-blue [&_svg]:size-3.5 [&_svg]:stroke-[2.5] ${spreadsheetData.isAdmin ? 'cursor-pointer' : ''}`}
      aria-label={join ? `Looked up from ${join.joinTableName}` : 'Looked up from another table'}
      role={spreadsheetData.isAdmin ? 'button' : 'img'}
      tabIndex={spreadsheetData.isAdmin ? 0 : undefined}
      data-testid="column-lookup-indicator"
      onClick={(event) => {
        event.stopPropagation()
        if (spreadsheetData.isAdmin) open()
      }}
      onKeyDown={(event) => {
        if (!spreadsheetData.isAdmin || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        event.stopPropagation()
        open()
      }}
    >
      <Join />
    </span>
  )

  return (
    <HoverCard
      plain
      title={
        join ? (
          <>
            Looked up from <TableName name={join.joinTableName} />
          </>
        ) : (
          'Looked up from another table'
        )
      }
      content={
        <span className="flex flex-col gap-2">
          {join && <LookupSentence join={join} columnId={columnId} />}
          {spreadsheetData.isAdmin && <span className="text-xs">Press to change this lookup.</span>}
        </span>
      }
    >
      {mark}
    </HoverCard>
  )
}

export default LookupIndicator
